import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type {
  Photo,
  PhotoWithDetails,
  PhotoCaptureData,
} from '../../../features/photos/types/photo'
import { photoService } from '../../../features/photos/services/photoService'
import { logout } from './authSlice'

interface PhotosState {
  items: PhotoWithDetails[]
  currentPhoto: PhotoWithDetails | null
  loading: boolean
  error: string | null
  uploadProgress: number
}

const initialState: PhotosState = {
  items: [],
  currentPhoto: null,
  loading: false,
  error: null,
  uploadProgress: 0,
}

// Async thunks
export const fetchPhotosByJobId = createAsyncThunk(
  'photos/fetchPhotosByJobId',
  async (jobId: string) => {
    return await photoService.getPhotosByJobId(jobId)
  }
)

export const fetchPhotosByNodeId = createAsyncThunk(
  'photos/fetchPhotosByNodeId',
  async (nodeId: string) => {
    return await photoService.getPhotosByNodeId(nodeId)
  }
)

export const capturePhoto = createAsyncThunk(
  'photos/capturePhoto',
  async (captureData: PhotoCaptureData) => {
    return await photoService.capturePhoto(captureData)
  }
)

export const updatePhoto = createAsyncThunk(
  'photos/updatePhoto',
  async ({ id, updates }: { id: string; updates: Partial<Photo> }) => {
    return await photoService.updatePhoto(id, updates)
  }
)

export const deletePhoto = createAsyncThunk('photos/deletePhoto', async (id: string) => {
  await photoService.deletePhoto(id)
  return id
})

// Slice
const photosSlice = createSlice({
  name: 'photos',
  initialState,
  reducers: {
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload
    },
    clearCurrentPhoto: (state) => {
      state.currentPhoto = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearPhotos: (state) => {
      state.items = []
      state.currentPhoto = null
    },
  },
  extraReducers: (builder) => {
    // Fetch photos by job ID
    builder
      .addCase(fetchPhotosByJobId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPhotosByJobId.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload
      })
      .addCase(fetchPhotosByJobId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch photos'
      })

    // Fetch photos by node ID
    builder
      .addCase(fetchPhotosByNodeId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPhotosByNodeId.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload
      })
      .addCase(fetchPhotosByNodeId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch photos'
      })

    // Capture photo
    builder
      .addCase(capturePhoto.pending, (state) => {
        state.loading = true
        state.error = null
        state.uploadProgress = 0
      })
      .addCase(capturePhoto.fulfilled, (state, action) => {
        state.loading = false
        state.uploadProgress = 100
        state.items.push(action.payload as PhotoWithDetails)
      })
      .addCase(capturePhoto.rejected, (state, action) => {
        state.loading = false
        state.uploadProgress = 0
        state.error = action.error.message || 'Failed to capture photo'
      })

    // Update photo
    builder
      .addCase(updatePhoto.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePhoto.fulfilled, (state, action) => {
        state.loading = false
        const index = state.items.findIndex((photo) => photo.id === action.payload.id)
        if (index !== -1) {
          state.items[index] = action.payload as PhotoWithDetails
        }
        if (state.currentPhoto?.id === action.payload.id) {
          state.currentPhoto = action.payload as PhotoWithDetails
        }
      })
      .addCase(updatePhoto.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update photo'
      })

    // Delete photo
    builder
      .addCase(deletePhoto.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deletePhoto.fulfilled, (state, action) => {
        state.loading = false
        state.items = state.items.filter((photo) => photo.id !== action.payload)
        if (state.currentPhoto?.id === action.payload) {
          state.currentPhoto = null
        }
      })
      .addCase(deletePhoto.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete photo'
      })

    // Clear photos state on logout
    builder.addCase(logout.fulfilled, (state) => {
      state.items = []
      state.currentPhoto = null
      state.loading = false
      state.error = null
      state.uploadProgress = 0
    })
  },
})

export const { setUploadProgress, clearCurrentPhoto, clearError, clearPhotos } = photosSlice.actions
export const photosReducer = photosSlice.reducer
